import type { DateTimeUnit, DurationUnit } from 'luxon';

import { Unicodes } from '@snowit/iceconst';

import { DateTime, Interval } from 'luxon';

type SnowmentInput = string | number | DateTime | Snowment | null | Date | undefined;
type SnowmentParams = {
  millis?: boolean;
};

// Keep the locale in this module instead of relying on Luxon's process-wide
// default. This lets the application change language without recreating the
// date library configuration, and also avoids formatting dates in English by
// accident when `toFormat` is used.
let snowmentLocale = 'it';

export const setSnowmentLocale = (locale: string): void => {
  snowmentLocale = locale;
};

export type SnowmentFormat =
  | /** 01/01/2022 */ 'short_date'
  | /** 12:30 */ 'time'
  | /** 01/01/2022 12:30 */ 'short_date_time'
  | /** 12:30:45 */ 'time_full'
  | /** 1 gen 2022 */ 'full_date'
  | /** 1 gen 2022, 12:30:45 */ 'full_date_time'
  | /** 01/01/2022 */ 'iso_date'
  | /** 01/01/2022, 12:30 */ 'iso_date_time'
  | /** 01 gen */ 'very_short';

export type SnowmentUnit =
  | 'd'
  | 'days'
  | 'y'
  | 'years'
  | 'm'
  | 'months'
  | 'minutes'
  | 'seconds'
  | 'hours'
  | 'h'
  | 'weeks'
  | 'w';

type Duration = {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total_seconds: number;
  future: boolean;
  total: (unit: DurationUnit) => number;
};

const BASIC_FORMAT = 'yyyy-MM-dd';

const mapUnits = (input: SnowmentUnit): DurationUnit => {
  switch (input) {
    case 'y':
    case 'years':
      return 'years';
    case 'm':
    case 'months':
      return 'months';
    case 'w':
    case 'weeks':
      return 'weeks';
    case 'minutes':
    case 'seconds':
      return input;
    case 'h':
    case 'hours':
      return 'hours';
    case 'd':
    case 'days':
    default:
      return 'days';
  }
};

export class Snowment {
  public date: DateTime;

  constructor(input: SnowmentInput, params: SnowmentParams = {}) {
    if (!input) {
      this.date = DateTime.now().setLocale(snowmentLocale);
    } else if (typeof input === 'string') {
      this.date = DateTime.fromISO(input).setLocale(snowmentLocale);
    } else if (typeof input === 'number') {
      if (params.millis) {
        this.date = DateTime.fromMillis(input).setLocale(snowmentLocale);
      } else {
        this.date = DateTime.fromSeconds(input).setLocale(snowmentLocale);
      }
    } else if (input instanceof Date) {
      this.date = DateTime.fromJSDate(input).setLocale(snowmentLocale);
    } else if (input instanceof DateTime) {
      this.date = input.setLocale(snowmentLocale);
    } else if (input instanceof Snowment) {
      this.date = input.date.setLocale(snowmentLocale);
    } else {
      this.date = DateTime.now().setLocale(snowmentLocale);
    }
  }

  public get year(): number {
    return this.date.year;
  }

  /** Month number [1-12] */
  public get month(): number {
    return this.date.month;
  }

  /** Day number [1-31] */
  public get day(): number {
    return this.date.day;
  }
  /** Day of the week [1-7]
   * 1 is Monday, 7 is Sunday */
  public get weekday(): number {
    return this.date.weekday;
  }

  public get hour(): number {
    return this.date.hour;
  }

  public get minute(): number {
    return this.date.minute;
  }

  public get second(): number {
    return this.date.second;
  }

  /** Epoch
   * (number of seconds since 01/01/1970) */
  public get epoch(): number {
    return this.date.toUnixInteger();
  }

  /** Epoch in milliseconds
   * (number of millisecods since 01/01/1970) */
  public get millis(): number {
    return this.date.toMillis();
  }

  /** The date as an instance of javascript Date */
  public get js(): Date {
    return this.date.toJSDate();
  }

  public get days_in_month(): number {
    return this.date.daysInMonth ?? 0;
  }

  public get valid(): boolean {
    return this.date.isValid;
  }

  libFormat(how: string): string {
    return this.date.toFormat(how);
  }

  format(how?: SnowmentFormat): string {
    switch (how) {
      case 'full_date':
        return this.date.toLocaleString(DateTime.DATE_MED);
      case 'full_date_time':
        return this.date.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
      case 'iso_date':
        return this.date.toLocaleString(DateTime.DATE_SHORT);
      case 'iso_date_time':
        return this.date.toLocaleString(DateTime.DATETIME_SHORT);
      case 'short_date':
        return this.date.toFormat('dd/MM/yyyy');
      case 'short_date_time':
        return this.date.toFormat('dd/MM/yyyy HH:mm');
      case 'time':
        return this.date.toLocaleString(DateTime.TIME_24_SIMPLE);
      case 'time_full':
        return this.date.toLocaleString(DateTime.TIME_24_WITH_SECONDS);
      case 'very_short':
        return this.date.toFormat('dd MMM');
      default:
        return this.date.toFormat(BASIC_FORMAT);
    }
  }

  add(quantity: number, unit: SnowmentUnit = 'd'): Snowment {
    const u = mapUnits(unit);
    this.date = this.date.plus({ [u]: quantity });
    return this;
  }

  subtract(quantity: number, unit: SnowmentUnit = 'd'): Snowment {
    const u = mapUnits(unit);
    this.date = this.date.minus({ [u]: quantity });
    return this;
  }

  startOf(unit: DateTimeUnit): DateTime {
    return this.date.startOf(unit);
  }

  endOf(unit: DateTimeUnit): DateTime {
    return this.date.endOf(unit);
  }

  isSameOrBefore(input?: SnowmentInput, unit?: DateTimeUnit): boolean {
    const compareWith = new Snowment(input);
    if (!unit) return this.date <= compareWith.date;
    return this.date.startOf(unit) <= compareWith.startOf(unit);
  }

  isBefore(input?: SnowmentInput, unit?: DateTimeUnit): boolean {
    const compareWith = new Snowment(input);
    if (!unit) return this.date < compareWith.date;
    return this.date.startOf(unit) < compareWith.startOf(unit);
  }

  isSameOrAfter(input?: SnowmentInput, unit?: DateTimeUnit): boolean {
    const compareWith = new Snowment(input);
    if (!unit) return this.date >= compareWith.date;
    return this.date.startOf(unit) >= compareWith.startOf(unit);
  }

  isAfter(input?: SnowmentInput, unit?: DateTimeUnit): boolean {
    const compareWith = new Snowment(input);
    if (!unit) return this.date > compareWith.date;
    return this.date.startOf(unit) > compareWith.startOf(unit);
  }

  isSame(input?: SnowmentInput, unit?: DateTimeUnit): boolean {
    const compareWith = new Snowment(input);
    if (!unit) return this.date.toMillis() === compareWith.date.toMillis();
    return this.date.startOf(unit).toMillis() === compareWith.startOf(unit).toMillis();
  }

  isBetween(first?: SnowmentInput, second?: SnowmentInput): boolean {
    const start = this.min(first, second);
    const end = this.max(first, second);
    return this.date > start.date && this.date < end.date;
  }

  isSameOrBetween(first?: SnowmentInput, second?: SnowmentInput): boolean {
    const start = this.min(first, second);
    const end = this.max(first, second);
    return this.date >= start.date && this.date <= end.date;
  }

  max(...inputs: SnowmentInput[]): Snowment {
    if (!inputs.length) return this;
    const snowments = inputs.map((input) => new Snowment(input).millis);
    const ret = Math.max(...snowments);
    return new Snowment(ret, { millis: true });
  }

  min(...inputs: SnowmentInput[]): Snowment {
    if (!inputs.length) return this;
    const snowments = inputs.map((input) => new Snowment(input).millis);
    const ret = Math.min(...snowments);
    return new Snowment(ret, { millis: true });
  }

  interval(input?: SnowmentInput) {
    const { date } = new Snowment(input);
    const min = this.min(this.date, date).date;
    const max = this.max(this.date, date).date;
    return Interval.fromDateTimes(min, max);
  }

  diff(input?: SnowmentInput): Duration {
    const { date } = new Snowment(input);
    const df = this.date.diff(date, [
      'years',
      'months',
      'days',
      'hours',
      'minutes',
      'milliseconds',
    ]);

    const future = date >= this.date;
    const interval = this.interval(date);
    const sourceDate = this.date;
    const total = (unit: DurationUnit): number => {
      const value = sourceDate.diff(date, unit).as(unit);
      // Moment truncates diff results toward zero for numeric units.
      return value < 0 ? Math.ceil(value) : Math.floor(value);
    };

    return {
      years: df.years,
      months: df.months,
      days: df.days,
      hours: df.hours,
      minutes: df.minutes,
      seconds: df.seconds,
      total_seconds: Math.floor(interval.length('seconds')),
      future,
      total,
    };
  }

  getStartAndEndOfMonth(): [string, string] {
    const start = this.date.startOf('month').toFormat(BASIC_FORMAT);
    const end = this.date.endOf('month').toFormat(BASIC_FORMAT);

    return [start, end];
  }

  getStartAndEndOfWeek(): [string, string] {
    const start = this.date.startOf('week').toFormat(BASIC_FORMAT);
    const end = this.date.endOf('week').toFormat(BASIC_FORMAT);

    return [start, end];
  }
}

const snowment = (input?: SnowmentInput, params?: SnowmentParams) => new Snowment(input, params);

snowment.enumerateDays = (
  first: SnowmentInput,
  second: SnowmentInput,
  format?: SnowmentFormat,
): string[] => {
  let start = new Snowment(first).min(first, second);
  const end = new Snowment(second).max(first, second);
  const dates: string[] = [];
  const diff = Interval.fromDateTimes(start.date, end.date);
  for (let i = 0; i <= Math.abs(diff.length('days')); i++) {
    dates.push(start.format(format));
    start = start.add(1, 'days');
  }
  return dates;
};

snowment.pretty = (first: SnowmentInput, second: SnowmentInput): string => {
  const start = new Snowment(first).min(first, second);
  const end = new Snowment(second).max(first, second);
  if (start.year !== end.year) {
    return [start.format('short_date'), end.format('short_date')].join(Unicodes.ARROW_RIGHT);
  }
  if (start.month !== end.month) {
    return [start.libFormat('dd MMM'), end.libFormat('dd MMM, yyyy')].join(Unicodes.ARROW_RIGHT);
  }
  if (start.day === end.day) return start.libFormat('dd MMM yyyy');
  return [start.libFormat('dd'), end.libFormat('dd, MMM yyyy')].join(Unicodes.ARROW_RIGHT);
};

snowment.epoch = (unit?: SnowmentUnit): number => {
  const now = Date.now();
  const seconds = Math.floor(now / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);
  switch (unit) {
    case 'd':
    case 'days':
      return days;
    case 'h':
    case 'hours':
      return hours;
    case 'm':
    case 'minutes':
      return minutes;
    case 'months':
      return months;
    case 'seconds':
      return seconds;
    case 'w':
    case 'weeks':
      return 0;
    case 'y':
    case 'years':
      return years;
    default:
      return now;
  }
};

export default snowment;
